// Activity metadata and validators. Pure helpers — no React, no Firestore.

export const activityFieldTypes = {
  'Aerobics': ['time'],
  'Arnold Shoulder Press': ['reps', 'time', 'weightLifted'],
  'Balance Exercises': ['time'],
  'Barbell Rows': ['reps', 'time', 'weightLifted'],
  'Barbell Shoulder Press': ['reps', 'time', 'weightLifted'],
  'Bear Crawls': ['reps', 'time'],
  'Bench Press': ['reps', 'time', 'weightLifted'],
  'Bicep Curls': ['reps', 'time', 'weightLifted'],
  'Boxing': ['time'],
  'Burpees': ['reps', 'time'],
  'Calf Raises': ['reps', 'time', 'weightLifted'],
  'Cat Cow Stretch': ['time'],
  'Childs Pose': ['time'],
  'Chin Ups': ['reps', 'time'],
  'Cobra Pose': ['time'],
  'Cross Country Skiing': ['time'],
  'Crunches': ['reps', 'time'],
  'Cycling': ['time'],
  'Dancing': ['time'],
  'Deadlifts': ['reps', 'time', 'weightLifted'],
  'Decline Bench Press': ['reps', 'time', 'weightLifted'],
  'Dips': ['reps', 'time'],
  'Downward Dog': ['time'],
  'Dumbbell Flies': ['reps', 'time', 'weightLifted'],
  'Dumbbell Rows': ['reps', 'time', 'weightLifted'],
  'Dynamic Stretching': ['time'],
  'Elliptical': ['time'],
  'Foam Rolling': ['time'],
  'Front Delt Raise': ['reps', 'time', 'weightLifted'],
  'Functional Movement Drills': ['time'],
  'Hammer Curls': ['reps', 'time', 'weightLifted'],
  'Hamstring Stretch': ['time'],
  'High Knees': ['reps', 'time'],
  'Hiking': ['time'],
  'Hip Openers': ['time'],
  'Incline Bench Press': ['reps', 'time', 'weightLifted'],
  'Iso-Lateral High Row': ['reps', 'time', 'weightLifted'],
  'Jogging': ['time'],
  'Jump Rope': ['time'],
  'Jumping Jacks': ['reps', 'time'],
  'Kickboxing': ['time'],
  'Lat Pulldown': ['reps', 'time', 'weightLifted'],
  'Lateral Delt Raise': ['reps', 'time', 'weightLifted'],
  'Leg Curl': ['reps', 'weightLifted'],
  'Leg Extension': ['reps', 'weightLifted'],
  'Leg Press': ['reps', 'time', 'weightLifted'],
  'Leg Raises': ['reps', 'time'],
  'Lunges': ['reps', 'time', 'weightLifted'],
  'Lunges Bodyweight': ['reps', 'time'],
  'Mobility Drills': ['time'],
  'Mountain Climbers': ['reps', 'time'],
  'Overhead Press': ['reps', 'time', 'weightLifted'],
  'Pigeon Pose': ['time'],
  'Pilates': ['time'],
  'Plank': ['time'],
  'Pull Ups': ['reps', 'time'],
  'Push Ups': ['reps', 'time'],
  'Rear Delt Fly': ['reps', 'time', 'weightLifted'],
  'Rowing': ['time'],
  'Running': ['time'],
  'Seated Cable Row': ['reps', 'time', 'weightLifted'],
  'Seated Forward Bend': ['time'],
  'Shoulder Mobility': ['time'],
  'Side Plank': ['time'],
  'Sit Ups': ['reps', 'time'],
  'Skating': ['time'],
  'Skull Crushers': ['reps', 'time', 'weightLifted'],
  'Spinning': ['time'],
  'Sprinting': ['time'],
  'Squat Jumps': ['reps', 'time'],
  'Squats': ['reps', 'time', 'weightLifted'],
  'Stair Climbing': ['time'],
  'Step Ups': ['reps', 'time'],
  'Stretching': ['time'],
  'Swimming': ['time'],
  'Tai Chi': ['time'],
  'Tricep Dips': ['reps', 'time', 'weightLifted'],
  'Walking': ['time'],
  'Wall Sits': ['time'],
  'Yoga': ['time'],
  'Zottman Curls': ['reps', 'time', 'weightLifted'],
  'Zumba': ['time']
};

export function displayActivityName(activityType) {
  if (!activityType) return '';
  return activityType
    .split(/[_-\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Formats a smart "Details" string for an activity record based on its type.
// Uses activityFieldTypes to determine which fields are relevant.
// count = reps value, time = minutes value, activityType = display name.
export function formatActivityDetails({ count, time, activityType }) {
  const fields = activityFieldTypes[displayActivityName(activityType)] || ['time'];
  const hasReps = fields.includes('reps') && count > 0;
  const hasTime = fields.includes('time') && time > 0;

  if (hasReps && hasTime) return `${count} reps · ${time} min`;
  if (hasReps) return `${count} reps`;
  if (hasTime) return `${time} min`;
  return '—';
}

export function validateActivity(activity) {
  const fields = activityFieldTypes[displayActivityName(activity.type)] || ['time'];
  if (fields.includes('time') && activity.time && (isNaN(parseInt(activity.time)) || parseInt(activity.time) <= 0)) {
    return 'Time must be a positive number.';
  }
  if (fields.includes('reps') && activity.reps && (isNaN(parseInt(activity.reps)) || parseInt(activity.reps) <= 0)) {
    return 'Reps must be a positive number if provided.';
  }
  if (fields.includes('weightLifted') && activity.weightLifted && (isNaN(parseInt(activity.weightLifted)) || parseInt(activity.weightLifted) < 0)) {
    return 'Weight lifted must be a non-negative number.';
  }
  return null;
}

export function validateWeight(weight) {
  const weightValue = parseFloat(weight);
  if (isNaN(weightValue) || weightValue <= 0) return 'Weight must be a positive number.';
  return null;
}

export function validateProfile(profileData) {
  const targetWeight = parseFloat(profileData.targetWeight);
  const height = parseFloat(profileData.height);
  const dob = profileData.dateOfBirth;
  const targetDate = profileData.targetDate ? new Date(profileData.targetDate) : null;

  if (targetWeight && (isNaN(targetWeight) || targetWeight <= 0)) return 'Target weight must be a positive number.';
  if (height && (isNaN(height) || height <= 0)) return 'Height must be a positive number.';
  if (dob) {
    const birthDate = new Date(dob);
    const today = new Date();
    if (birthDate > today) return 'Date of birth cannot be in the future.';
  }
  if (targetDate && targetDate < new Date()) return 'Target date cannot be in the past.';
  return null;
}
